import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import { Checkbox, Stack, Tooltip, Typography } from '@lyearn/ui';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import { ColumnShape } from '@/components/Table/types';
import LearnerListItem from '@/modules/corporate/modules/assignment/views/AssignmentCreator/components/SelectLearnersDialog/components/ListItem';
import LearnerGroupListItem from '@/modules/corporate/modules/group/components/GroupListItem';
import {
  ArrayOperationCommandEnum,
  GroupStatusEnum,
  GroupTypeEnum,
  UserCollectionGroupType,
  UserType,
} from '@/types/schema';

import { LearnerAndGroups, Tabs, UseAsyncListSelectAllManagerReturnType } from './types';

export const OVERRIDES = {
  Tabs: {
    classes: { flexContainer: 'space-s-24' },
  },
  Tab: {
    classes: { root: 'min-w-0 p-0 opacity-100 heading-xs py-8 normal-case' },
  },
};

export const getTabs = ({
  t,
  isGroupVisible,
  isUserVisible,
}: {
  t: TFunction;
  isGroupVisible: boolean;
  isUserVisible: boolean;
}) =>
  getEnabledItems([
    {
      label: t('Admin:LearnerAndGroups.Learners'),
      value: Tabs.Learners,
      enabled: isUserVisible,
    },
    {
      label: t('Admin:LearnerAndGroups.Groups'),
      value: Tabs.Groups,
      enabled: isGroupVisible,
    },
  ]);

export const TRANSLATIONS = {
  [Tabs.Learners]: {
    title: 'Admin:LearnerAndGroups.NoLearners',
    description: 'Admin:LearnerAndGroups.NoLearnersDescription',
  },
  [Tabs.Groups]: {
    title: 'Admin:LearnerAndGroups.NoGroup',
    description: 'Admin:LearnerAndGroups.NoGroupDescription',
  },
};

export const getTableColumns = ({
  t,
  isSelectAll,
  isPartiallySelected,
  onSelectAll,
  inViewTab,
  isItemSelected,
  toggle,
  selectedCount,
  disabled,
}: {
  t: TFunction;
  isSelectAll: boolean;
  isPartiallySelected: boolean;
  onSelectAll: () => void;
  isItemSelected: (id: string, isInGroups?: boolean) => boolean;
  inViewTab: Tabs;
  toggle: (id: string, isSelected: boolean, userGroupId?: string, isInGroups?: boolean) => void;
  selectedCount: number;
  disabled?: boolean;
}): ColumnShape<LearnerAndGroups>[] => [
  {
    key: 'statusAndName',
    headerRenderer: () =>
      inViewTab === Tabs.Groups ? (
        <Stack className="w-full">
          <Stack
            alignItems="center"
            className="w-full border-b border-divider pb-12 pt-2 ps-12 pe-20"
            justifyContent="between">
            <Typography color="text-text-placeholder" variant="label-caps">
              {t('Common:SelectedCount2', { count: selectedCount })}
            </Typography>
            <Tooltip
              classes={{
                tooltip: '!max-w-[30.6rem]',
              }}
              placement="left"
              title={t('Common:SelectAllDescription')}>
              <Checkbox
                checked={isSelectAll}
                data-attr="SELECT"
                disabled={disabled}
                indeterminate={isPartiallySelected}
                size="medium"
                onChange={onSelectAll}
              />
            </Tooltip>
          </Stack>
        </Stack>
      ) : null,
    cellRenderer: ({ rowData }) => {
      const currentContent = rowData;

      const { id } = currentContent;

      if (inViewTab === Tabs.Learners) {
        const isInGroup = (rowData as any)?.isPresentInGroup ?? false;
        const isInGroups = (rowData as any)?.isInGroups;
        const isSelected = isItemSelected(id, isInGroups);
        return (
          <LearnerListItem
            key={id}
            classes={{
              container: 'w-full',
            }}
            disabled={disabled || isInGroup}
            selected={isSelected || isInGroup}
            user={currentContent as UserType}
            onSelect={() => toggle(id, !isSelected, '', isInGroups)}
          />
        );
      }

      const isSelected = isItemSelected(id);
      return (
        <LearnerGroupListItem
          key={id}
          classes={{
            container: 'w-full',
          }}
          disabled={disabled}
          group={currentContent as UserCollectionGroupType}
          selected={isSelected}
          onSelect={() => toggle(id, !isSelected)}
        />
      );
    },
    width: 800,
    flexGrow: 1,
  },
];

export const GROUP_DEFAULT_FILTERS = {
  systemGenerated: false,
  status: [GroupStatusEnum.Published],
  types: [GroupTypeEnum.UserCollection],
};

export const getGroupSelectionConfig = (
  groupData: Pick<UseAsyncListSelectAllManagerReturnType<any>, 'asyncList'>,
  filterManager: ReturnType<typeof useFilterManagerWithState>,
) => {
  /**
   * When no action is performed, or no selection is made
   */
  if (
    groupData.asyncList.state.excludeIds.length === 0 &&
    groupData.asyncList.state.includeIds.length === 0 &&
    !groupData.asyncList.state.isSelectAll
  ) {
    return undefined;
  }

  return {
    excludeIds: groupData.asyncList.state.excludeIds,
    includeIds: groupData.asyncList.state.includeIds,
    operation: ArrayOperationCommandEnum.Add,
    filter: groupData.asyncList.state.includeIds.length
      ? undefined
      : {
          ...GROUP_DEFAULT_FILTERS,
          ...filterManager.state.filter,
          name: filterManager.state.search,
        },
  };
};
